import type {
  ArticleDetail,
  ArticleImportResult,
  ArticleProgressView,
  ArticleSentenceView,
  ArticleSummary,
  Paginated,
  TextbookCatalogNode,
  TextbookSummary,
  UnitSummary,
} from '@en3/shared';
import { api } from './client';

/**
 * 内容接口（教材 / 单元 / 文章 / 阅读进度）。
 *
 * 三条约定：
 * 1. **路径只在 `@/app/routes` 里定义页面路由**，这里的 `/api/...` 路径是接口路径，
 *    两者刻意分开：将来把 API 换到子域时，只需要改这个文件。
 * 2. 列表接口一律返回 `Paginated<T>`，「有没有下一页」由 `totalPages` 决定，
 *    前端不要自己用 `items.length < pageSize` 猜。
 * 3. 免费可读的接口（目录、文章）不传 token 也能调；`api/client` 会在有令牌时自动带上。
 */

export interface ArticleListParams {
  page?: number;
  pageSize?: number;
  textbookId?: string;
  unitId?: string;
  level?: string;
  q?: string;
  onlyLong?: boolean;
  scope?: 'me' | 'public' | 'all';
}

/** 把查询参数拼成字符串：`undefined` 与 `''` 都不出现在 URL 里。 */
export function toQuery(params: Record<string, unknown> | object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text.length > 0 ? `?${text}` : '';
}

export const contentApi = {
  /** 教材目录：`withUnits` 时一次拿到「教材 → 单元 → 文章」整棵树。 */
  catalog: (params: { level?: string; withUnits?: boolean } = {}) =>
    api.get<{ items: TextbookCatalogNode[] | TextbookSummary[] }>(`/api/textbooks${toQuery(params)}`),

  textbook: (id: string) =>
    api.get<{ textbook: TextbookSummary; units: UnitSummary[] }>(`/api/textbooks/${id}`),

  units: (textbookId: string) => api.get<{ items: UnitSummary[] }>(`/api/textbooks/${textbookId}/units`),

  createTextbook: (input: Record<string, unknown>) =>
    api.post<{ textbook: TextbookSummary }>('/api/textbooks', input),

  createUnit: (textbookId: string, input: Record<string, unknown>) =>
    api.post<{ unit: UnitSummary }>(`/api/textbooks/${textbookId}/units`, input),

  /** 装载内置目录（幂等）。只有管理员能调，目录页在 403 时会隐藏该按钮。 */
  seedTextbooks: () => api.post<{ textbooks: number; units: number; created: number }>('/api/textbooks/seed'),

  articles: (params: ArticleListParams = {}) =>
    api.get<Paginated<ArticleSummary>>(`/api/articles${toQuery(params)}`),

  article: (id: string) => api.get<{ article: ArticleDetail }>(`/api/articles/${id}`),

  /**
   * 导入文章。
   *
   * `acknowledgeCopyright` 由调用方在 UI 上显式勾选后传 true —— 后端拿不到它就会返回
   * 422 `COPYRIGHT_ACK_REQUIRED`，这里刻意**不给默认值**，避免"顺手就替用户确认了授权"。
   */
  importArticle: (input: {
    title: string;
    content: string;
    acknowledgeCopyright: true;
    titleZh?: string;
    author?: string;
    source?: string;
    sourceUrl?: string;
    licenseNote?: string;
    textbookId?: string;
    unitId?: string;
    visibility?: 'public' | 'private';
    importSource?: 'paste' | 'upload' | 'url' | 'builtin';
  }) => api.post<ArticleImportResult>('/api/articles/import', input),

  updateArticle: (id: string, input: Record<string, unknown>) =>
    api.patch<{ article: ArticleSummary }>(`/api/articles/${id}`, input),

  deleteArticle: (id: string) => api.delete<{ deleted: boolean }>(`/api/articles/${id}`),

  /** 阅读进度上报（增量语义，见后端 upsertProgress）。 */
  reportProgress: (
    articleId: string,
    input: { lastGlobalIndex?: number; readSecondsDelta?: number; finished?: boolean; selfRating?: number },
  ) => api.post<{ progress: unknown }>(`/api/articles/${articleId}/progress`, input),

  /** 某一篇的长难句（阶段 2 端点，阅读页与长难句页共用）。 */
  articleLongSentences: (articleId: string) =>
    api.get<{ article: ArticleSummary; sentences: ArticleSentenceView[] }>(
      `/api/articles/${articleId}/long-sentences`,
    ),

  /** 继续学习：最近读过的文章 + 各自的进度。 */
  recent: (limit = 4) =>
    api.get<{ items: { article: ArticleSummary; progress: ArticleProgressView }[] }>(
      `/api/me/recent${toQuery({ limit })}`,
    ),
};

export interface RecentArticle {
  article: ArticleSummary;
  progress: ArticleProgressView;
}
